import React, {
    useCallback,
    useMemo,
    useState,
    useEffect,
    useRef,
} from 'react';
import ReactFlow, {
    Node,
    Edge,
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    NodeTypes,
    EdgeTypes,
    BackgroundVariant,
    NodeChange,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import {
    TopologyData,
    TopologyLink,
    BaselineData,
    LinkLabelFields,
    DEFAULT_LINK_LABEL_FIELDS,
    SptResult,
    AlgoLinkMode,
    AlgoMap,
    resolveAlgoLocatorName,
    FLEX_ALGO_TRAFFIC_COLOR,
    FLEX_ALGO_SPF_BOTH_COLOR,
    FLEX_ALGO_DIM_COLOR,
    TOPOLOGY_LINK_STROKE,
    getRawBaselineLink,
    isTopologyLinkOfflineOrDropped,
    resolveLinkBandwidth,
} from '../TopologyData';
import {
    AlgoHighlight,
    linkParticipatesInAlgoSubgraph,
} from '../utils/algoFilter';
import SiteGroupNode from './SiteGroupNode';
import RouterNode from './RouterNode';
import LinkEdge from './LinkEdge';
import { resolveSiteCenters } from '../utils/siteAnchors';

/**
 * Persisted layout for the topology canvas.
 *
 * The shape is purely additive — each entry can carry any subset of
 * `{ x, y, w, h }` — so a new field never needs a migration. The key went
 * to `v2` when the default site placement moved to fixed anchors
 * (`utils/siteAnchors.ts`): a `v1` record would have pinned every site to
 * its old dagre spot and hidden the new layout, so it is left behind on
 * purpose. "Reset layout" clears `v2` the same way.
 *
 * Indexed by:
 *   - `site-<siteId>` for site groups → `{ x?, y?, w?, h? }`
 *   - `<routerId>`    for routers      → `{ x?, y? }` (parent-relative,
 *                                         RF "extent: parent" coord space)
 */
type LayoutEntry = {
    x?: number;
    y?: number;
    w?: number;
    h?: number;
};
type SavedLayout = Record<string, LayoutEntry>;

const LAYOUT_STORAGE_KEY = 'kontrol.topology.siteLayout.v2';

function loadSavedLayout(): SavedLayout {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function saveLayout(layout: SavedLayout): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(
            LAYOUT_STORAGE_KEY,
            JSON.stringify(layout)
        );
    } catch {
        // Storage may be unavailable; ignore.
    }
}

function clearSavedLayout(): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(LAYOUT_STORAGE_KEY);
    } catch {
        // Ignore.
    }
}

interface Props {
    data: TopologyData;
    baseline: BaselineData | null;
    /** Unfiltered baseline payload — used to detect offline/dropped links. */
    rawBaseline?: BaselineData | null;
    /** Synthetic links whose IS-IS adjacency dropped entirely (present in
     *  baseline, absent from the snapshot). Rendered with the offline
     *  treatment alongside the live links. */
    downLinks?: TopologyLink[];
    onNodeClick: (nodeId: string) => void;
    onEdgeClick: (linkId: string) => void;
    pathMode?: boolean;
    onPathSelect?: (source: string, target: string) => void;
    onSitePathSelect?: (sourceSite: string, targetSite: string) => void;
    linkFields?: LinkLabelFields;
    sptResult?: SptResult | null;
    sptMode?: boolean;
    onSptRootSelect?: (nodeId: string) => void;
    excludedLinks?: Set<string>;
    excludedNodes?: Set<string>;
    simulationMode?: boolean;
    onToggleExcludeLink?: (linkId: string) => void;
    onToggleExcludeNode?: (nodeId: string) => void;
    trafficOverlay?: boolean;
    packetLossOverlay?: boolean;
    lastUpdate?: string;
    demoMode?: boolean;
    onDemoToggle?: () => void;
    p2pSource?: string | null;
    p2pTarget?: string | null;
    p2pEdgeIds?: Set<string> | null;
    p2pNodeIds?: Set<string> | null;
    onClearP2p?: () => void;
    /** Optional Flex-Algo highlight: dims non-participating nodes and
     *  colours links by traffic / SPF / both. When `null` or
     *  `active === false`, the graph behaves exactly as before. */
    algoHighlight?: AlgoHighlight | null;
    /** From ke-topology — used for flex-algo display names in the banner. */
    algoMap?: AlgoMap;
}

const nodeTypes: NodeTypes = {
    siteGroup: SiteGroupNode,
    routerNode: RouterNode,
};

const edgeTypes: EdgeTypes = {
    linkEdge: LinkEdge,
};

const NODE_W = 100;
const NODE_H = 96;
// Gap between sibling routers — wide enough to host an inline link label
// (e.g. "1 µs / 100G", roughly 60-70px wide) for adjacent connections.
// Longer intra-site links that skip a card will lift the label above instead.
const GAP = 78;
const PAD_X = 24;
const PAD_TOP = 48;
const PAD_BOTTOM = 28;

function buildNodes(
    data: TopologyData,
    savedLayout: SavedLayout = {}
): Node[] {
    const { topology, nodes: allNodes } = data;
    const sites = topology.sites;

    // --- 1. Site dimensions (compact, multi-column) ----------------
    const siteDims: Record<string, { w: number; h: number }> = {};
    sites.forEach((site) => {
        const cols = Math.min(site.nodes.length, 3);
        const rows = Math.ceil(site.nodes.length / cols);
        const w = cols * NODE_W + (cols - 1) * GAP + PAD_X * 2;
        const h = rows * NODE_H + (rows - 1) * GAP + PAD_TOP + PAD_BOTTOM;
        siteDims[site.id] = { w, h };
    });

    // --- 2. Dagre site-level layout --------------------------------
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({
        rankdir: 'LR',
        // ranksep = horizontal distance between site columns (LR layout).
        // Generous so inter-site labels have room to breathe and lines that
        // would otherwise cross unrelated cards have room to route around.
        ranksep: 1300,
        nodesep: 480,
        marginx: 160,
        marginy: 140,
        edgesep: 140,
    });

    sites.forEach((site) => {
        const d = siteDims[site.id];
        g.setNode(site.id, { width: d.w, height: d.h });
    });

    const edgeSet = new Set<string>();
    topology.interSiteLinks.forEach((link) => {
        const src = allNodes.find((n) => n.id === link.source);
        const tgt = allNodes.find((n) => n.id === link.target);
        if (src && tgt && src.site !== tgt.site) {
            const key = [src.site, tgt.site].sort().join('--');
            if (!edgeSet.has(key)) {
                edgeSet.add(key);
                g.setEdge(src.site, tgt.site);
            }
        }
    });

    dagre.layout(g);

    // Known sites sit on fixed anchors; dagre only places newcomers.
    const siteCenters = resolveSiteCenters(
        sites.map((site) => site.id),
        (siteId) => g.node(siteId)
    );

    // --- 3. Build React Flow nodes --------------------------------
    const rfNodes: Node[] = [];

    sites.forEach((site) => {
        const pos = siteCenters[site.id];
        const dimDefault = siteDims[site.id];

        // Restore any persisted layout for this site. Each axis falls
        // back independently so a partial entry (e.g. only `{w,h}` from
        // a resize without a drag, or only `{x,y}` from a v1 record)
        // is rendered correctly.
        const saved = savedLayout[`site-${site.id}`];
        const sitePosition = {
            x: saved?.x ?? pos.x - dimDefault.w / 2,
            y: saved?.y ?? pos.y - dimDefault.h / 2,
        };
        const siteWidth = saved?.w ?? dimDefault.w;
        const siteHeight = saved?.h ?? dimDefault.h;

        rfNodes.push({
            id: `site-${site.id}`,
            type: 'siteGroup',
            position: sitePosition,
            data: {
                label: site.location.datacenterId || site.id,
                location: site.location,
                nodeCount: site.nodes.length,
            },
            style: { width: siteWidth, height: siteHeight },
            zIndex: 1,
            draggable: true,
            selectable: false,
        });

        const cols = Math.min(site.nodes.length, 3);
        site.nodes.forEach((sn, i) => {
            const col = i % cols;
            const row = Math.floor(i / cols);

            // Routers persist their position in the same SavedLayout
            // store, keyed by the router's own id. Position is parent-
            // relative because the node uses `extent: 'parent'` — it
            // matches RF's coord space exactly, no transform needed.
            const savedRouter = savedLayout[sn.id];
            const routerX = savedRouter?.x ?? PAD_X + col * (NODE_W + GAP);
            const routerY = savedRouter?.y ?? PAD_TOP + row * (NODE_H + GAP);

            rfNodes.push({
                id: sn.id,
                type: 'routerNode',
                parentNode: `site-${site.id}`,
                extent: 'parent' as const,
                position: { x: routerX, y: routerY },
                data: {
                    hostname: sn.id,
                    role: sn.role,
                    nodeType: sn.type,
                    isLocal: sn.isLocal,
                    site: site.id,
                },
                zIndex: 50,
                // Routers are draggable inside their parent site.
                // `extent: 'parent'` (above) constrains the position to
                // the site's bounds, so the router can never escape its
                // container.
                draggable: true,
            });
        });
    });

    return rfNodes;
}

const TopologyGraph: React.FC<Props> = ({
    data,
    baseline,
    rawBaseline = null,
    downLinks = [],
    onNodeClick,
    onEdgeClick,
    pathMode,
    onPathSelect,
    onSitePathSelect,
    linkFields = DEFAULT_LINK_LABEL_FIELDS,
    sptResult,
    sptMode,
    onSptRootSelect,
    excludedLinks,
    excludedNodes,
    simulationMode,
    onToggleExcludeLink,
    onToggleExcludeNode,
    trafficOverlay,
    packetLossOverlay,
    lastUpdate,
    demoMode,
    onDemoToggle,
    p2pSource,
    p2pTarget,
    p2pEdgeIds,
    p2pNodeIds,
    onClearP2p,
    algoHighlight,
    algoMap,
}) => {
    const [pathSource, setPathSource] = useState<string | null>(null);

    /**
     * Single source of truth for "which site is the user focused on".
     * Per the gesture spec, only one site can be selected at a time —
     * a single nullable string trivially enforces that. Writes:
     *   - site click            → setSelectedSiteId(siteId)
     *   - router/reflector click → setSelectedSiteId(parentSiteId)
     *   - empty canvas click    → setSelectedSiteId(null)
     */
    const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

    useEffect(() => {
        if (!pathMode) setPathSource(null);
    }, [pathMode]);

    /**
     * Persisted layout for sites and routers.
     * See SavedLayout / LayoutEntry above for the shape — every field
     * is optional so that an entry holding only `{x,y}` (the original
     * v1 shape) still loads cleanly alongside one holding `{w,h}` or
     * `{x,y,w,h}`.
     */
    const [savedLayout, setSavedLayout] = useState<SavedLayout>(() =>
        loadSavedLayout()
    );

    const initialNodes = useMemo(
        () => buildNodes(data, savedLayout),
        [data, savedLayout]
    );

    /**
     * Build the inter-site connections with directional handle picks.
     * For two sites, all links between them are routed through a small
     * pre-computed pool of handle pairs (left/right or top/bottom),
     * cycled per link so parallel edges don't overlap.
     */
    const initialEdges = useMemo<Edge[]>(() => {
        const baselineLinks = baseline?.links ?? {};

        // Map node → site id, and node → (row, col) within its site.
        const nodeSite: Record<string, string> = {};
        const nodePos: Record<string, { row: number; col: number }> = {};
        data.topology.sites.forEach((site) => {
            const cols = Math.min(site.nodes.length, 3);
            site.nodes.forEach((sn, i) => {
                nodeSite[sn.id] = site.id;
                nodePos[sn.id] = {
                    row: Math.floor(i / cols),
                    col: i % cols,
                };
            });
        });

        // Site center positions (computed by dagre)
        const sitePos: Record<string, { x: number; y: number; w: number; h: number }> = {};
        for (const n of initialNodes) {
            if (n.id.startsWith('site-')) {
                const sid = n.id.replace('site-', '');
                const w = (n.style?.width as number) ?? 0;
                const h = (n.style?.height as number) ?? 0;
                sitePos[sid] = {
                    x: n.position.x + w / 2,
                    y: n.position.y + h / 2,
                    w,
                    h,
                };
            }
        }

        // Per-link throughput
        const linkThroughputs: Record<string, number> = {};
        let totalThroughput = 0;
        for (const link of data.links) {
            const fIn = link.forward?.inboundMbps ?? null;
            const fOut = link.forward?.outboundMbps ?? null;
            const rIn = link.reverse?.inboundMbps ?? null;
            const rOut = link.reverse?.outboundMbps ?? null;
            const rx = fIn != null || rIn != null ? Math.max(fIn ?? 0, rIn ?? 0) : 0;
            const tx = fOut != null || rOut != null ? Math.max(fOut ?? 0, rOut ?? 0) : 0;
            const t = rx + tx;
            linkThroughputs[link.id] = t;
            totalThroughput += t;
        }

        // Group inter-site links by site pair so we can spread parallel edges.
        const pairKey = (a: string, b: string) => [a, b].sort().join('--');
        const pairCounts: Record<string, number> = {};
        const pairIndex: Record<string, number> = {};
        for (const link of [...data.links, ...downLinks]) {
            const a = nodeSite[link.source];
            const b = nodeSite[link.target];
            if (!a || !b || a === b) continue;
            const k = pairKey(a, b);
            pairCounts[k] = (pairCounts[k] ?? 0) + 1;
        }

        type HandleCombo = { src: string; tgt: string };

        // Horizontal handle pools: source on right, target on left. Used
        // when target site is to the right of source.
        const horizontalR: HandleCombo[] = [
            { src: 'right', tgt: 'left' },
            { src: 'right', tgt: 'top' },
            { src: 'bottom', tgt: 'left' },
            { src: 'right', tgt: 'bottom' },
            { src: 'top', tgt: 'left' },
        ];
        const horizontalL: HandleCombo[] = [
            { src: 'left-src', tgt: 'right-tgt' },
            { src: 'left-src', tgt: 'top' },
            { src: 'bottom', tgt: 'right-tgt' },
            { src: 'left-src', tgt: 'bottom-tgt' },
            { src: 'top-src', tgt: 'right-tgt' },
        ];
        const verticalD: HandleCombo[] = [
            { src: 'bottom', tgt: 'top' },
            { src: 'right', tgt: 'top' },
            { src: 'bottom', tgt: 'left' },
            { src: 'left-src', tgt: 'top' },
            { src: 'bottom', tgt: 'right-tgt' },
        ];
        const verticalU: HandleCombo[] = [
            { src: 'top-src', tgt: 'bottom-tgt' },
            { src: 'right', tgt: 'bottom-tgt' },
            { src: 'top-src', tgt: 'left' },
            { src: 'left-src', tgt: 'bottom-tgt' },
            { src: 'top-src', tgt: 'right-tgt' },
        ];

        /**
         * Perpendicular control-point spread between parallel edges on the
         * same site pair. With curved (Bezier) inter-site edges, this acts
         * as the curvature magnitude — symmetric around 0 so parallels arc
         * to opposite sides and don't overlap.
         */
        const LABEL_SPREAD = 110;

        return [...data.links, ...downLinks].map((link) => {
            const bl = baselineLinks[link.id];
            const rawBl = getRawBaselineLink(rawBaseline, link.id);
            const linkOffline = isTopologyLinkOfflineOrDropped(link, rawBl);
            const srcSite = nodeSite[link.source];
            const tgtSite = nodeSite[link.target];
            const isIntra = srcSite === tgtSite;

            const sharePct =
                totalThroughput > 0
                    ? ((linkThroughputs[link.id] ?? 0) / totalThroughput) * 100
                    : null;

            let sourceHandle = 'right';
            let targetHandle = 'left';
            let labelOffset = 0;

            if (!isIntra && sitePos[srcSite] && sitePos[tgtSite]) {
                const k = pairKey(srcSite, tgtSite);
                const count = pairCounts[k] ?? 1;
                const idx = pairIndex[k] ?? 0;
                pairIndex[k] = idx + 1;

                const dx = sitePos[tgtSite].x - sitePos[srcSite].x;
                const dy = sitePos[tgtSite].y - sitePos[srcSite].y;

                let pool: HandleCombo[];
                if (Math.abs(dx) >= Math.abs(dy)) {
                    pool = dx >= 0 ? horizontalR : horizontalL;
                } else {
                    pool = dy >= 0 ? verticalD : verticalU;
                }

                const combo = pool[idx % pool.length];
                sourceHandle = combo.src;
                targetHandle = combo.tgt;

                // Center labels around the segment, then spread by index.
                const center = (count - 1) / 2;
                labelOffset = (idx - center) * LABEL_SPREAD;
            }

            const handles = edgeHandleMap();
            const bw = resolveLinkBandwidth(link);

            return {
                id: link.id,
                source: link.source,
                target: link.target,
                sourceHandle: isIntra ? handles.src : sourceHandle,
                targetHandle: isIntra ? handles.tgt : targetHandle,
                type: 'linkEdge',
                data: {
                    avgDelayUs: link.avgDelayUs,
                    bandwidth: bw.label,
                    bandwidthShort: bw.shortLabel,
                    // Always derive from actual site assignment — guarantees
                    // intra-site links use the intra-site rendering path.
                    isInterSite: !isIntra,
                    baselineUs: bl?.avgDelayUs ?? null,
                    linkOffline,
                    lastSeenUp: rawBl?.lastSeenAt ?? null,
                    forwardUs: link.forward?.avgDelayUs ?? null,
                    reverseUs: link.reverse?.avgDelayUs ?? null,
                    forwardLoss: link.forward?.lossPercent ?? null,
                    reverseLoss: link.reverse?.lossPercent ?? null,
                    forwardInboundMbps: link.forward?.inboundMbps ?? null,
                    forwardOutboundMbps: link.forward?.outboundMbps ?? null,
                    reverseInboundMbps: link.reverse?.inboundMbps ?? null,
                    reverseOutboundMbps: link.reverse?.outboundMbps ?? null,
                    isisMetric: link.metric,
                    networkSharePct: sharePct,
                    sourceDesc: link.sourceInterfaceDesc,
                    targetDesc: link.targetInterfaceDesc,
                    onLabelClick: simulationMode
                        ? () => onToggleExcludeLink?.(link.id)
                        : () => onEdgeClick(link.id),
                    linkFields,
                    sptTreeEdge:
                        sptResult?.treeEdgeIds.has(link.id) ?? false,
                    sptActive: !!sptResult,
                    excluded: excludedLinks?.has(link.id) ?? false,
                    trafficOverlay: !!trafficOverlay,
                    packetLossOverlay: !!packetLossOverlay,
                    bandwidthKbps: bw.kbps,
                    p2pPathEdge: p2pEdgeIds?.has(link.id) ?? false,
                    p2pActive: !!p2pEdgeIds,
                    labelOffset,
                    // Flex-algo highlight: per-link membership in the
                    // traffic / SPF sets. The renderer dims non-members
                    // and colours members by mode. All three flags read
                    // as `false`/`'off'` when no algo is selected, so
                    // existing behaviour is preserved.
                    algoFilterActive: algoHighlight?.active ?? false,
                    algoLinkMode: (algoHighlight?.mode ?? 'off') as AlgoLinkMode,
                    algoTrafficLink:
                        algoHighlight?.trafficLinkIds.has(link.id) ?? false,
                    algoSpfLink:
                        algoHighlight?.spfLinkIds.has(link.id) ?? false,
                    algoInParticipantSubgraph: linkParticipatesInAlgoSubgraph(
                        link,
                        algoHighlight
                    ),
                },
                animated: false,
            };
        });
    }, [
        data,
        baseline,
        rawBaseline,
        downLinks,
        onEdgeClick,
        linkFields,
        sptResult,
        excludedLinks,
        simulationMode,
        onToggleExcludeLink,
        trafficOverlay,
        packetLossOverlay,
        p2pEdgeIds,
        initialNodes,
        algoHighlight,
    ]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    // Re-apply node positions if the topology data or saved layout changes
    // (without losing in-flight drag state).
    useEffect(() => {
        setNodes(initialNodes);
    }, [initialNodes, setNodes]);

    useEffect(() => {
        setEdges(initialEdges);
    }, [initialEdges, setEdges]);

    /**
     * Buffer for the in-flight resize dimensions.
     *
     * NodeResizer (see node_modules/@reactflow/node-resizer/.../index.mjs)
     * fires two distinct kinds of `dimensions` change:
     *   - during drag : { ...., dimensions: {width, height}, resizing: true }
     *   - on  mouseup : { id, type: 'dimensions', resizing: false }
     *                   (no `dimensions` field at all)
     *
     * The width/height the user actually wants to commit live ONLY in
     * the `resizing: true` frames. The end frame is just a "stop"
     * signal. So we cache the latest live size in this ref on every
     * tick (no state update → no rebuild loop, no broken gesture) and
     * flush it to `savedLayout` once the end frame arrives.
     *
     * Keyed by site id (`site-<id>`); cleared after each flush.
     */
    const liveResizeDimsRef = useRef<
        Record<string, { w: number; h: number }>
    >({});

    /**
     * Wrap onNodesChange to persist any of the three things the user
     * can change about a node:
     *   1. site drag end           → record { x, y }
     *   2. site resize end         → record { w, h }  (from buffer above)
     *   3. router drag end         → record { x, y }  (parent-relative)
     *
     * Each axis is *merged* into the existing record (`...next[id]`)
     * so a router drag never wipes the saved site size, and a site
     * drag never wipes the saved router positions.
     */
    const handleNodesChange = useCallback(
        (changes: NodeChange[]) => {
            onNodesChange(changes);

            // Bucket the changes we care about. We walk `changes` once
            // to collect intent, then resolve final values from the
            // freshest node state inside the setNodes callback below.
            const positionEndIds: string[] = [];
            const dimensionUpdates: Record<
                string,
                { w: number; h: number }
            > = {};

            for (const c of changes) {
                if (c.type === 'position' && c.dragging === false) {
                    // Drag (site OR router) just ended.
                    positionEndIds.push(c.id);
                } else if (
                    c.type === 'dimensions' &&
                    c.id.startsWith('site-')
                ) {
                    // Three sub-cases keyed off `resizing`:
                    //   true      → live drag — buffer the size, do NOT
                    //               trigger a state update; that would
                    //               kick off the initialNodes memo +
                    //               setNodes(initialNodes) effect mid-
                    //               gesture and break NodeResizer's
                    //               grip on the handle.
                    //   false     → end of drag — `c` carries no
                    //               dimensions, so we flush the buffered
                    //               size from liveResizeDimsRef.
                    //   undefined → RF's own measurement (mount, style
                    //               update); ignored.
                    if (c.resizing === true && c.dimensions) {
                        liveResizeDimsRef.current[c.id] = {
                            w: Math.round(c.dimensions.width),
                            h: Math.round(c.dimensions.height),
                        };
                    } else if (c.resizing === false) {
                        const buffered =
                            liveResizeDimsRef.current[c.id];
                        if (buffered) {
                            dimensionUpdates[c.id] = buffered;
                            delete liveResizeDimsRef.current[c.id];
                        }
                    }
                }
            }

            if (
                positionEndIds.length === 0 &&
                Object.keys(dimensionUpdates).length === 0
            ) {
                return;
            }

            // Read the latest absolute positions from the freshest
            // node state. (Reading via setNodes callback avoids stale
            // closures around `nodes`.)
            setNodes((current) => {
                setSavedLayout((prev) => {
                    const next: SavedLayout = { ...prev };

                    for (const id of positionEndIds) {
                        const node = current.find((n) => n.id === id);
                        if (!node) continue;
                        next[id] = {
                            ...next[id],
                            x: node.position.x,
                            y: node.position.y,
                        };
                    }

                    for (const [id, dim] of Object.entries(
                        dimensionUpdates
                    )) {
                        next[id] = {
                            ...next[id],
                            w: dim.w,
                            h: dim.h,
                        };
                    }

                    saveLayout(next);
                    return next;
                });
                return current;
            });
        },
        [onNodesChange, setNodes]
    );

    const handleResetLayout = useCallback(() => {
        clearSavedLayout();
        setSavedLayout({});
    }, []);

    const handleNodeClick = useCallback(
        (_: React.MouseEvent, node: Node) => {
            const isSiteNode = node.id.startsWith('site-');

            // Selection rules from the gesture spec:
            //  - clicking a site selects that site
            //  - clicking a router/reflector selects its parent site
            //    (in addition to the router's own action)
            // Writing here unconditionally implements "only one site at a
            // time" — the new value displaces any previous selection.
            // RF's `nodeDragThreshold={5}` (set on <ReactFlow>) guarantees
            // this branch only runs on a true click; any movement past
            // 5 px commits to a drag and onNodeClick is suppressed by RF.
            if (isSiteNode) {
                setSelectedSiteId(node.id);
            } else if (node.parentNode) {
                setSelectedSiteId(node.parentNode);
            }

            if (simulationMode && !isSiteNode) {
                onToggleExcludeNode?.(node.id);
                return;
            }

            if (sptMode && !isSiteNode) {
                onSptRootSelect?.(node.id);
                return;
            }

            if (pathMode) {
                const clickId = node.id;
                const clickIsSite = isSiteNode;

                if (!pathSource) {
                    setPathSource(clickId);
                } else {
                    const srcIsSite = pathSource.startsWith('site-');

                    if (srcIsSite && clickIsSite && onSitePathSelect) {
                        const srcSiteId = pathSource.replace('site-', '');
                        const tgtSiteId = clickId.replace('site-', '');
                        if (srcSiteId !== tgtSiteId) {
                            onSitePathSelect(srcSiteId, tgtSiteId);
                        }
                        setPathSource(null);
                    } else if (!srcIsSite && !clickIsSite && onPathSelect) {
                        if (clickId !== pathSource) {
                            onPathSelect(pathSource, clickId);
                        }
                        setPathSource(null);
                    } else {
                        setPathSource(clickId);
                    }
                }
                return;
            }

            if (isSiteNode) return;
            onNodeClick(node.id);
        },
        [
            onNodeClick,
            pathMode,
            onPathSelect,
            onSitePathSelect,
            pathSource,
            sptMode,
            onSptRootSelect,
            simulationMode,
            onToggleExcludeNode,
        ]
    );

    const handleEdgeClick = useCallback(
        (_: React.MouseEvent, edge: Edge) => {
            onEdgeClick(edge.id);
        },
        [onEdgeClick]
    );

    /**
     * Empty-canvas click → deselect the active site (gesture spec).
     * RF differentiates this from a pan: a drag on the pane fires
     * onMove / onPan, while a press-release without movement fires
     * onPaneClick.
     */
    const handlePaneClick = useCallback(() => {
        setSelectedSiteId(null);
    }, []);

    const displayNodes = useMemo(() => {
        return nodes.map((n) => {
            const extra: Record<string, unknown> = {};
            // RF copies `node.className` onto the outer `.react-flow__node`
            // wrapper. We piggy-back on it to mark the selected site so
            // the global cursor CSS (`.is-selected { cursor: default }`)
            // can win over RF's default `cursor: grab` on the wrapper.
            let extraClassName: string | undefined;

            if (n.id === selectedSiteId) {
                extra.isSelected = true;
                extraClassName = 'is-selected';
            }

            if (pathMode && pathSource && n.id === pathSource) {
                extra.pathHighlight = 'source' as const;
            }

            if (sptResult && sptResult.nodes[n.id]) {
                extra.sptCost = sptResult.nodes[n.id].cost;
                extra.sptRoot = n.id === sptResult.root;
            }

            if (excludedNodes?.has(n.id)) {
                extra.excluded = true;
            }

            if (p2pNodeIds) {
                extra.p2pActive = true;
                extra.p2pOnPath = p2pNodeIds.has(n.id);
                if (n.id === p2pSource) extra.p2pRole = 'source';
                else if (n.id === p2pTarget) extra.p2pRole = 'target';
            }

            // Flex-algo Level-1 highlight: only routers (not site
            // groups) participate in the algo set. Site groups stay
            // untouched so the container remains visually anchored.
            if (algoHighlight?.active && n.type === 'routerNode') {
                extra.algoFilterActive = true;
                extra.algoIsParticipant = algoHighlight.nodeIds.has(n.id);
            }

            if (
                Object.keys(extra).length === 0 &&
                extraClassName === undefined
            ) {
                return n;
            }
            return {
                ...n,
                ...(extraClassName !== undefined
                    ? { className: extraClassName }
                    : {}),
                data: { ...n.data, ...extra },
            };
        });
    }, [
        nodes,
        pathMode,
        pathSource,
        sptResult,
        excludedNodes,
        p2pNodeIds,
        p2pSource,
        p2pTarget,
        selectedSiteId,
        algoHighlight,
    ]);

    return (
        <div
            className="bg-semantic-bg-primary"
            style={{ width: '100%', height: '100%', position: 'relative' }}
        >
            {/* Z-stack and the global cursor map for the gesture spec.
                Lines below cards: edges drawn beneath, labels float on top.

                Cursor map per surface (single CSS source of truth, with
                !important so it wins over RF's wrapper defaults and any
                Tailwind utility on inner descendants):
                  - canvas idle / panning  → grab / grabbing
                  - site idle / dragging   → grab / grabbing
                  - site selected body     → default
                  - router idle / dragging → pointer / grabbing
                  - resize handles         → directional cursors come from
                                              NodeResizer per handle
            */}
            <style>{`
                .react-flow__edges { z-index: 1 !important; }
                .react-flow__nodes { z-index: 2 !important; }
                .react-flow__edgelabelrenderer { z-index: 5 !important; }

                .react-flow__pane              { cursor: grab !important; }
                .react-flow__pane.dragging     { cursor: grabbing !important; }

                .react-flow__node-siteGroup            { cursor: grab !important; }
                .react-flow__node-siteGroup.dragging   { cursor: grabbing !important; }
                .react-flow__node-siteGroup.is-selected{ cursor: default !important; }

                .react-flow__node-routerNode           { cursor: pointer !important; }
                .react-flow__node-routerNode.dragging  { cursor: grabbing !important; }

                .react-flow__controls button {
                    background: var(--color-semantic-bg-primary) !important;
                    border: 1px solid var(--color-semantic-border-secondary) !important;
                    color: var(--color-semantic-text-secondary) !important;
                }
                .react-flow__controls button:hover {
                    background: var(--color-semantic-bg-secondary) !important;
                    color: var(--color-semantic-text-primary) !important;
                }
            `}</style>

            {/* Header overlay: title + legend */}
            <div
                className="absolute top-0 left-0 right-0 z-10
                    flex items-center justify-between gap-4
                    px-4 py-3 pointer-events-none"
            >
                <div className="flex items-center gap-3 pointer-events-auto">
                    <h3 className="text-sm font-semibold text-semantic-text-primary">
                        Network topology
                        {' — '}
                        <span className="text-semantic-text-secondary font-medium">
                            KE backbone
                        </span>
                    </h3>
                    {Object.keys(savedLayout).length > 0 && (
                        <button
                            type="button"
                            onClick={handleResetLayout}
                            className="flex items-center gap-1 text-[11px]
                                font-semibold rounded-md border
                                border-semantic-border-secondary
                                text-semantic-text-secondary
                                bg-semantic-bg-primary/95
                                px-2 py-1 shadow-sm hover:text-semantic-text-primary
                                hover:border-semantic-border-primary
                                transition-colors"
                            title="Restore the auto-computed layout"
                        >
                            <svg
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="1 4 1 10 7 10" />
                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                            </svg>
                            Reset layout
                        </button>
                    )}
                </div>
                <div
                    className="flex items-center gap-4 text-[11px]
                        text-semantic-text-secondary pointer-events-auto
                        bg-semantic-bg-primary/95 px-3 py-1.5 rounded-lg
                        border border-semantic-border-secondary shadow-sm
                        backdrop-blur-sm"
                >
                    <LegendDash
                        color={TOPOLOGY_LINK_STROKE.success}
                        label={
                            packetLossOverlay
                                ? 'No packet loss'
                                : trafficOverlay
                                ? 'Normal traffic'
                                : 'Base latency'
                        }
                    />
                    <LegendDash
                        color={TOPOLOGY_LINK_STROKE.warning}
                        label={
                            packetLossOverlay
                                ? 'Low packet loss'
                                : trafficOverlay
                                ? 'High traffic'
                                : 'High latency'
                        }
                    />
                    {!packetLossOverlay && (
                        <LegendDash
                            color={TOPOLOGY_LINK_STROKE.danger}
                            label={
                                trafficOverlay
                                    ? 'Critical traffic'
                                    : 'Critical latency'
                            }
                        />
                    )}
                    {!packetLossOverlay && !trafficOverlay && (
                        <LegendDash
                            color={TOPOLOGY_LINK_STROKE.offline}
                            label="Offline / dropped"
                        />
                    )}
                    {packetLossOverlay && (
                        <LegendDash
                            color={TOPOLOGY_LINK_STROKE.danger}
                            label="High packet loss"
                        />
                    )}
                    {algoHighlight?.active &&
                        algoHighlight.mode !== 'off' && (
                        <>
                            {(algoHighlight.mode === 'traffic' ||
                                algoHighlight.mode === 'both') && (
                                <LegendDash
                                    color={FLEX_ALGO_TRAFFIC_COLOR}
                                    label="Traffic path"
                                    dash="none"
                                />
                            )}
                            {(algoHighlight.mode === 'spf' ||
                                algoHighlight.mode === 'both') && (
                                <LegendDash
                                    color={
                                        algoHighlight.mode === 'both'
                                            ? FLEX_ALGO_SPF_BOTH_COLOR
                                            : FLEX_ALGO_TRAFFIC_COLOR
                                    }
                                    label="SPF path"
                                    dash="8 4"
                                />
                            )}
                            <LegendDash
                                color={FLEX_ALGO_DIM_COLOR}
                                label="Other links"
                                dash="6 4"
                            />
                        </>
                    )}
                    <LegendIcon
                        color="var(--color-semantic-fg-brand-primary)"
                        label="Router"
                        shape="rect"
                    />
                    <LegendIcon
                        color="var(--color-semantic-fg-danger)"
                        label="Reflect."
                        shape="circle"
                    />
                </div>
            </div>

            {/* Mode banners */}
            {p2pSource && p2pTarget && (
                <div
                    className="absolute top-14 left-1/2 -translate-x-1/2 z-10
                        flex items-center gap-3 px-4 py-2 rounded-lg text-xs
                        font-semibold shadow-lg border"
                    style={{
                        background: 'var(--color-semantic-bg-primary)',
                        borderColor: '#ff4089',
                        color: 'var(--color-semantic-text-primary)',
                    }}
                >
                    <span style={{ color: '#ff4089' }}>P2P</span>
                    <span>{p2pSource}</span>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#ff4089"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                    </svg>
                    <span>{p2pTarget}</span>
                    {onClearP2p && (
                        <button
                            onClick={onClearP2p}
                            className="ml-1 p-0.5 rounded hover:bg-semantic-bg-secondary
                                transition-colors cursor-pointer"
                            style={{ lineHeight: 0 }}
                            title="Clear point-to-point"
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    )}
                </div>
            )}
            {pathMode && !p2pSource && (
                <div
                    className="absolute top-14 left-1/2 -translate-x-1/2 z-10
                        px-4 py-2 rounded-lg text-xs font-semibold
                        bg-semantic-bg-brand-primary text-semantic-text-brand
                        border border-semantic-border-brand shadow-md"
                >
                    {pathSource
                        ? `Source: ${pathSource.replace('site-', '')} — Click destination ${pathSource.startsWith('site-') ? 'site' : 'node'}`
                        : 'Click a router or site group'}
                </div>
            )}
            {sptMode && !sptResult && (
                <div
                    className="absolute top-14 left-1/2 -translate-x-1/2 z-10
                        px-4 py-2 rounded-lg text-xs font-semibold
                        bg-semantic-bg-brand-primary text-semantic-text-brand
                        border border-semantic-border-brand shadow-md"
                >
                    SPT Mode: Click a router to set as root
                </div>
            )}
            {simulationMode && (
                <div
                    className="absolute top-14 left-1/2 -translate-x-1/2 z-10
                        px-4 py-2 rounded-lg text-xs font-semibold
                        bg-semantic-bg-danger-primary text-semantic-text-danger
                        border border-semantic-border-danger shadow-md"
                >
                    Simulation: Click nodes/links to toggle failure
                </div>
            )}
            {algoHighlight?.active && !p2pSource && !pathMode && (
                <div
                    className="absolute top-14 right-3 z-10 flex items-center
                        gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold
                        bg-semantic-bg-brand-primary text-semantic-text-brand
                        border border-semantic-border-brand shadow-md"
                >
                    <span>
                        Flex-Algo{' '}
                        {algoHighlight.algorithm != null
                            ? `${algoHighlight.algorithm} (${resolveAlgoLocatorName(
                                  algoHighlight.algorithm,
                                  algoMap
                              )})`
                            : '—'}{' '}
                        · {algoHighlight.nodeIds.size} router
                        {algoHighlight.nodeIds.size === 1 ? '' : 's'}
                    </span>
                    {algoHighlight.mode !== 'off' && (
                        <span
                            className="text-[10px] uppercase tracking-wider
                                opacity-80"
                        >
                            ·{' '}
                            {algoHighlight.mode === 'both'
                                ? 'traffic + spf'
                                : algoHighlight.mode}
                            {algoHighlight.mode === 'traffic' ||
                            algoHighlight.mode === 'both'
                                ? ` · ${algoHighlight.trafficLinkIds.size} live`
                                : ''}
                            {algoHighlight.mode === 'spf' ||
                            algoHighlight.mode === 'both'
                                ? ` · ${algoHighlight.spfLinkIds.size} on SPT`
                                : ''}
                        </span>
                    )}
                </div>
            )}
            {sptMode && onDemoToggle && (
                <button
                    onClick={onDemoToggle}
                    className={`absolute top-14 left-3 z-10
                        flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold
                        border shadow-md transition-all duration-200 cursor-pointer ${
                            demoMode
                                ? 'bg-semantic-bg-brand-primary text-semantic-text-brand border-semantic-border-brand'
                                : 'bg-semantic-bg-primary text-semantic-text-secondary border-semantic-border-secondary hover:text-semantic-text-primary hover:border-semantic-border-primary'
                        }`}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        {demoMode ? (
                            <>
                                <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" stroke="none" />
                                <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" stroke="none" />
                            </>
                        ) : (
                            <polygon
                                points="6,4 20,12 6,20"
                                fill="currentColor"
                                stroke="none"
                            />
                        )}
                    </svg>
                    {demoMode ? 'Stop Demo' : 'Guided Demo'}
                </button>
            )}

            <ReactFlow
                nodes={displayNodes}
                edges={edges}
                onNodesChange={handleNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={handleNodeClick}
                onEdgeClick={handleEdgeClick}
                onPaneClick={handlePaneClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                minZoom={0.15}
                maxZoom={2.5}
                proOptions={{ hideAttribution: true }}
                nodesDraggable={true}
                // 5 px disambiguates click (select) from drag (move).
                // Press-release with < 5 px movement → onNodeClick fires
                // → site selected. Any movement ≥ 5 px → drag commits and
                // RF suppresses onNodeClick. Single global gesture state
                // is owned by RF — one mode at a time, no overlap.
                nodeDragThreshold={5}
            >
                <Background
                    variant={BackgroundVariant.Dots}
                    gap={24}
                    size={0.6}
                    className="!fill-semantic-text-secondary"
                    style={{ opacity: 0.18 }}
                />
                <Controls
                    showInteractive={false}
                    className="!bg-semantic-bg-primary
                        !border !border-semantic-border-secondary
                        !rounded-lg !shadow-sm"
                />
            </ReactFlow>
            {lastUpdate && (
                <div
                    className="absolute bottom-2 right-2 z-10 px-2 py-1 rounded-md
                        text-[10px] text-semantic-text-secondary/60"
                >
                    Updated {new Date(lastUpdate).toLocaleString()}
                </div>
            )}
        </div>
    );
};

/** Default intra-site handle pair (left → right). */
const INTRA_HANDLES = { src: 'right', tgt: 'left' } as const;
function edgeHandleMap(): { src: string; tgt: string } {
    return INTRA_HANDLES;
}

const LegendDash: React.FC<{
    color: string;
    label: string;
    /** SVG stroke-dasharray; `'none'` draws a solid line. */
    dash?: string;
}> = ({ color, label, dash = '5 3' }) => (
    <span className="flex items-center gap-1.5">
        <svg width="22" height="6" viewBox="0 0 22 6">
            <line
                x1="1"
                y1="3"
                x2="21"
                y2="3"
                stroke={color}
                strokeWidth="1.6"
                strokeDasharray={dash === 'none' ? undefined : dash}
                strokeLinecap="round"
            />
        </svg>
        <span>{label}</span>
    </span>
);

const LegendIcon: React.FC<{
    color: string;
    label: string;
    shape: 'rect' | 'circle';
}> = ({ color, label, shape }) => (
    <span className="flex items-center gap-1.5">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            {shape === 'rect' ? (
                <rect
                    x="2"
                    y="3.5"
                    width="10"
                    height="7"
                    rx="1"
                    stroke={color}
                    strokeWidth="1.4"
                />
            ) : (
                <circle
                    cx="7"
                    cy="7"
                    r="4.5"
                    stroke={color}
                    strokeWidth="1.4"
                    fill="none"
                />
            )}
        </svg>
        <span>{label}</span>
    </span>
);

export default TopologyGraph;
